import { modalActions } from '@/actions';
import Fab from '@/components/elements/forms/buttons/Fab/Fab';
import { usePinInput } from '@/components/elements/forms/PinInput/PinInput';
import Icon from '@/components/icons/Icon';
import CreateAccount from '@/components/templates/login/CreateAccount';
import RequestToken from '@/components/templates/login/RequestToken';
import VerifyOTP from '@/components/templates/login/VerifyOTP';
import VerifySuccess from '@/components/templates/login/VerifySuccess';
import { EVENT_NAME, SCREEN_NAME, SCREEN_VIEW } from '@/constants/analytics';
import { trackMpEvent } from '@/helpers';
import { useAppSelector } from '@/hooks';
import { LoginContext, LoginContextType, View } from '@/hooks/useLogin';
import useTrackScreenShown from '@/hooks/useTrackScreenShown';
import { _s } from '@/locale';
import { TriggerSource } from '@/types/analytics';
import { Dispatch, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Modal, { ModalProps } from './redesign/Modal/Modal';

type LoginModalProps = ModalProps & {
  floating?: boolean;
  source: TriggerSource;
};

export const baseTranslationKey = (view: View) => `loginModal.${view}`;

const closeLoginModal = (ctx: LoginContextType, dispatch: Dispatch<any>) => {
  dispatch(modalActions.login({ show: false }));
  ctx.clearLoginState();
  ctx.onClose();
};

const LoginModal = ({ isOpen, source }: LoginModalProps) => {
  const { view } = useContext(LoginContext);
  return (
    <Modal isOpen={isOpen} shouldCloseOnOverlayClick={false}>
      {view === 'requestToken' && <RequestTokenContent source={source} />}
      {view === 'createAccount' && <CreateAccountContent source={source} />}
      {view === 'verifyOtp' && <VerifyOTPContent source={source} />}
      {view === 'verifySuccess' && <VerifySuccessContent source={source} />}
    </Modal>
  );
};

const VerifySuccessContent = ({ source }: { source: TriggerSource }) => {
  const ctx = useContext(LoginContext);
  const { userProfileOnboarding } = useAppSelector((state) => state.modal);

  const dispatch = useDispatch();

  const handleCloseLoginModal = () => {
    closeLoginModal(ctx, dispatch);
  };

  useEffect(() => {
    if (
      source !== SCREEN_NAME.CHECKOUT_BOOKING_SUMMARY &&
      source !== SCREEN_NAME.CHECKOUT_BUNDLE_SUMMARY &&
      !userProfileOnboarding.show
    ) {
      trackMpEvent(EVENT_NAME.LOGIN_LOGGED_IN, { screen_name: SCREEN_VIEW.LOGIN_START, initiated_from: source });
    }
  }, []);

  return (
    <Modal.Content floating gradientBackground>
      <div className="gap-md flex flex-col">
        <h2 className="text-fg_primary text-h-m">{_s(`${baseTranslationKey(ctx.view)}.title`)}</h2>
        <VerifySuccess email={ctx.email} onAnimationComplete={handleCloseLoginModal} />
      </div>
    </Modal.Content>
  );
};

const VerifyOTPContent = ({ source }: { source: TriggerSource }) => {
  const { email, view, user, updateView, handleLogin, handleEmailResendClicked, clearLoginState } =
    useContext(LoginContext);

  const pinProps = usePinInput({ fields: 6, validateCallback: handleLogin });

  const handleGoBack = () => clearLoginState();

  useEffect(() => {
    if (user) updateView('verifySuccess');
  }, [user, view]);

  useEffect(() => {
    if (pinProps.error || pinProps.clipError) {
      trackMpEvent(EVENT_NAME.LOGIN_FAILED, {
        screen_name: SCREEN_VIEW.LOGIN_START,
        initiated_from: source,
      });
    }
  }, [pinProps.error, pinProps.clipError]);

  return (
    <Modal.Content floating gradientBackground>
      <div className="gap-md flex flex-col">
        <div className="gap-lg flex items-start">
          <Fab
            onClick={handleGoBack}
            variant="secondary"
            icon={<Icon variant="back" />}
            size="md"
            aria-label={_s('back')}
          />
        </div>
        <h2 className="text-fg_primary text-h-m">{_s(`${baseTranslationKey(view)}.title`)}</h2>
        <VerifyOTP {...pinProps} email={email} handleEmailResendClicked={handleEmailResendClicked} source={source} />
      </div>
    </Modal.Content>
  );
};

const RequestTokenContent = ({ source }: { source: TriggerSource }) => {
  const ctx = useContext(LoginContext);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  useTrackScreenShown({ screen_name: SCREEN_VIEW.LOGIN_START, initiated_from: source });

  const handleCloseLoginModal = () => {
    closeLoginModal(ctx, dispatch);
    trackMpEvent(EVENT_NAME.LOGIN_CANCELED, { screen_name: SCREEN_VIEW.LOGIN_START, initiated_from: source });
  };

  return (
    <Modal.Content floating>
      <div className="flex flex-col">
        <div className="gap-lg pb-xl flex items-start justify-between">
          <div className="bg-bg_info_bold grid place-content-center rounded-full" style={{ width: 56, height: 56 }}>
            <Icon variant="account" color="fg_alwaysLight" size="md" />
          </div>
          <Fab
            onClick={handleCloseLoginModal}
            variant="secondary"
            size="md"
            icon={<Icon variant="close-alt" />}
            aria-label={_s('close')}
          />
        </div>
        <h2 className="text-fg_primary text-h-m pb-sm">{_s(`${baseTranslationKey(ctx.view)}.title`)}</h2>
        <RequestToken {...ctx} source={source} redirectUrl={pathname} autoFocus />
      </div>
    </Modal.Content>
  );
};

const CreateAccountContent = ({ source }: { source: TriggerSource }) => {
  const ctx = useContext(LoginContext);
  const { pathname } = useLocation();

  return (
    <Modal.Content floating>
      <div className="gap-md flex flex-col">
        <div className="gap-lg flex items-start">
          <Fab
            onClick={() => ctx.updateView('requestToken')}
            variant="secondary"
            icon={<Icon variant="back" />}
            size="md"
            aria-label={_s('back')}
          />
        </div>
        <h2 className="text-fg_primary text-h-m">{_s(`${baseTranslationKey(ctx.view)}.title`)}</h2>
        <CreateAccount {...ctx} source={source} redirectUrl={pathname} />
      </div>
    </Modal.Content>
  );
};

export default LoginModal;
